import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IBondSubmitResponse,
  ISuretyProviderAPIResponse,
  ISelectedBondResponse,
  IBondApiRequest,
  IBondListResponse,
  IBondClaimsListResponse,
} from '../models/financial-security-api.model';
import { ISelectedBondDetail } from '../models/financial-security.model';
import { HttpParams } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { AdvancedSearchCheckboxes, AdvancedSearchConfigResponse, AdvancedSearchOption } from '@carm/shared/components/advanced-search/models/advanced-search.model';
import { IBaseResponse } from '@carm/_utils/models';
import { map } from 'rxjs/operators';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class SuretyProviderService extends BaseApiService {
  constructor(protected injector: Injector, private datePipe: DatePipe) {
    super(injector);
  }

  get serviceBaseUrlBP(): string {
    return `${this.endPointBP}/surety-provider`;
  }

  get serviceBaseUrlB(): string {
    return `${this.endPointB}/surety-provider`;
  }

  get serviceBaseUrlNB(): string {
    return `${this.getBaseUrl}/surety-provider`;
  }

  private get getBaseUrl(): string {
    return environment.baseApiUrls;
  }

  public getSubmittedBond(
    bn15: string,
    bondtype: string,
    number: string
  ): Observable<ISelectedBondResponse> {
    const type = bondtype === 'Cash' ? 'cash-bond' : 'e-bond'
    const endPoint = `${this.serviceBaseUrlB}/${type}/bond-id/${number}?bondProgramId=${bn15}`;
    return this.httpAuthGet<ISelectedBondResponse>(endPoint);
  }

  public submitEditBond(change: IBondApiRequest): Observable<IBaseResponse> {
    change.validFrom = undefined;
    const endPoint = `${this.serviceBaseUrlB}/bond/update`;
    return this.httpAuthPut<IBaseResponse>(endPoint, change);
  }

  public submitActionBond(
    action: string,
    bond: ISelectedBondDetail
  ): Observable<IBondSubmitResponse> {
    const endPoint = `${this.serviceBaseUrlB}/bond/` + action;
    // const endPoint = `${this.serviceUrlB}/program/${bn15}/financial/security/bond/` + action;
    const req = {
      bn15: bond.bn15,
      id: bond.id,
      type: bond.type,
      suretyProvider: bond.suretyProvider,
    };
    if (bond.type !== 'Cash') {
      req.suretyProvider = bond.suretyProvider;
    }
    return this.httpAuthPut<any>(endPoint, req);
  }

  public getPostedBond(
    minDate: Date,
    maxDate: Date,
    searchString: string = '',
    pageIndex,
    pageSize,
    sort
  ): Observable<IBondListResponse> {
    const params = new HttpParams()
      .set('currentPage', pageIndex)
      .set('pageSize', pageSize)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('bondsSort', sort)
      .set('searchString', searchString);
    const endPoint = `${this.serviceBaseUrlB}/bonds`;
    return this.httpAuthGet<IBondListResponse>(
      endPoint,
      params,
      LoaderSpinnerTypes.AgGrid
    );
  }

  public getPendingBonds(
    minDate: Date,
    maxDate: Date,
    searchString: string = '',
    pageIndex,
    pageSize,
    sort
  ): Observable<IBondListResponse> {
    const params = new HttpParams()
      .set('currentPage', pageIndex)
      .set('pageSize', pageSize)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('bondsSort', sort)
      .set('searchString', searchString);
    const endPoint = `${this.serviceBaseUrlB}/pending-approval-bonds`;
    return this.httpAuthGet<IBondListResponse>(
      endPoint,
      params,
      LoaderSpinnerTypes.AgGrid
    );
  }

  public getClaimsBond(
    minDate: Date,
    maxDate: Date,
    searchString: string = '',
    pageIndex,
    pageSize,
    sort
  ): Observable<IBondClaimsListResponse> {
    const params = new HttpParams()
      .set('currentPage', pageIndex)
      .set('pageSize', pageSize)
      .set('startDate', this.datePipe.transform(minDate, 'yyyy-MM-dd'))
      .set('endDate', this.datePipe.transform(maxDate, 'yyyy-MM-dd'))
      .set('bondsSort', sort)
      .set('searchString', searchString);
    const endPoint = `${this.serviceBaseUrlB}/bond-claims`;
    return this.httpAuthGet<IBondClaimsListResponse>(
      endPoint,
      params,
      LoaderSpinnerTypes.AgGrid
    );
  }

  public getSuretyProvider(): Observable<ISuretyProviderAPIResponse> {
    const endPoint = `${this.serviceBaseUrlNB}/list`;
    return this.httpAuthGet<ISuretyProviderAPIResponse>(endPoint);
  }

  public getPendingApprovalBondsCount(): Observable<number> {
    const endPoint = `${this.serviceBaseUrlB}/pending-approval-bonds-count`;
    return this.httpAuthGet<any>(endPoint);
  }

  getBondsSearchOptions(searchType: 'posted-bonds' | 'pending-bonds' | 'bonds-claims'): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.serviceBaseUrlNB}/${searchType}/search-options`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `financial-security.surety-dashboard.table.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `financial-security.surety-dashboard.table.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }
}
